import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import '../../styles/toolbar.scss'

import { ACCESS_GRANTED } from '../../actions/webrtcConnection'
import {
  audioChatStartRequest,
  audioChatStartRequestCancel,
  audioChatResetPeerStatus,
  audioChatMuteMicrophone,
  audioChatEnd,
  SPEAKING_MIN_AUDIO_LEVEL,
} from '../../actions/media'
import { tbShowModal, tbHideModal, tbCloseModal } from '../../actions/toolbar'
import { TB_MODALS } from './Toolbar'
import ToolbarButton from './ToolbarButton'
import DropdownModal from './DropdownModal'
import { AudioDevices } from '../AudioDevices'

export const AUDIO_CHAT_AREA = {
  CLIENT: 'chat-area',
  PEER: 'peer-status',
}
// show/hide media devices & options in request audio chat modal
// default false (use true only for debugging/options)
const showMediaDevices = false

class AudioChatComponent extends Component {

  toggleMute () {
    const { microphoneMuted, chatStarted } = this.props.audio
    // muteMicrophone action processed in
    //  - webrtcConnection-mw (send mute/unmute command to agent, enable/disable track)
    //  - audio reducer (save status)
    if (chatStarted) {
      this.props.muteMicrophoneAction(!microphoneMuted)
    }
  }

  toggleAudioChat () {
    const { chatStarted } = this.props.audio
    if (chatStarted) {
      this.props.audioChatEndAction({ endByPeer: false })
    } else {
      this.props.audioChatStartRequestAction({ requestByPeer: false })
    }
  }

  renderAudioChatRequestModal () {
    return (
      <DropdownModal
        title={this.context.t('chat-request-by-you-title')}
        onCloseBoxClick={() => this.props.tbHideModalAction(TB_MODALS.AUDIO_CHAT_REQUEST)}
        buttons={[
          {
            text: this.context.t('chat-btn-cancel'),
            onClick: () => {
              this.props.audioChatStartRequestCancelAction({ cancelByPeer: false })
            },
            className: 'tbCancel',
            disabled: this.props.mediaDevicesQueryInProgress,
          }
        ]}
      >
        <AudioDevices showMediaDevices={showMediaDevices} />
        <p>{this.context.t('chat-request-by-you-text')}</p>
      </DropdownModal>
    )
  }

  renderAudioChatRejectedModal () {
    // showing this modal is triggered from audioChat middleware
    // on audioChatStartResponse action
    const closeModal = () => {
      this.props.audioChatResetPeerStatusAction({ rejectByPeer: false })
      this.props.tbCloseModalAction(TB_MODALS.AUDIO_CHAT_REJECTED)
    }
    return (
      <DropdownModal
        title={this.context.t('chat-request-by-you-rejected-title')}
        onCloseBoxClick={closeModal}
        buttons={[
          {
            text: this.context.t('btn-close'),
            onClick: closeModal,
          }
        ]}
      >
        <p>{this.context.t('chat-request-by-you-rejected-text')}</p>
      </DropdownModal>
    )
  }

  handleAudioChatClick () {
    const { audio: { chatStarted, chatRequest, requestByPeer } } = this.props
    if (chatStarted) {
      this.props.audioChatEndAction({ endByPeer: false })
      return
    }

    if (!chatRequest) {
      this.props.audioChatStartRequestAction({ requestByPeer: false })
    } else if (!requestByPeer) {
      this.props.tbShowModalAction(TB_MODALS.AUDIO_CHAT_REQUEST)
      // showing this modal is also triggered from audioChat middleware
      // on reconnecting, if it was previously displayed
    }
  }

  renderAudioChat () {
    const {
      chatStarted,
      chatRequest,
      selectedAudioInput,
      microphoneMuted,
      requestByPeer,
      rejectByPeer,
      stats: { audioLevel }
    } = this.props.audio
    const { updated, audioInput: { notAllowed } } = this.props.mediaDevices
    const missing = (updated !== 0 && (!selectedAudioInput || !selectedAudioInput.track))

    const muted = missing || microphoneMuted

    let tooltip = null
    if (chatStarted) {
      tooltip = this.context.t('chat-tb-btn-end-title')
    } else {
      if (!chatRequest) {
        tooltip = this.context.t('chat-tb-btn-start-title')
      }
      if (chatRequest && !requestByPeer && this.props.modalStatus === 'hidden') {
        tooltip = this.context.t('chat-tb-btn-cancel-title')
      }
    }
    let microphoneTooltip = null
    if (missing) {
      microphoneTooltip = this.context.t('chat-tb-microphone-missing-title')
      if (notAllowed) {
        microphoneTooltip = this.context.t('chat-tb-microphone-not-allowed-title')
      }
    } else if (chatStarted) {
      microphoneTooltip = this.context.t('chat-tb-microphone-mute-title')
      if (muted) {
        microphoneTooltip = this.context.t('chat-tb-microphone-unmute-title')
      }
    }
    const microphoneClass = Object
      .entries({
        'tbMicrophone': true,
        'muted': muted,
        'missing': missing,
        // 'speaking': (!muted && audioLevel > 0.00006),
        'speaking': (!muted && audioLevel > SPEAKING_MIN_AUDIO_LEVEL),
      })
      .filter(([, truthy]) => truthy)
      .map(([className]) => className)
      .join(' ')

    let dropdownModalId = null
    let dropdownModal = null
    if (chatRequest && !requestByPeer) {
      dropdownModal = this.renderAudioChatRequestModal()
      dropdownModalId = TB_MODALS.AUDIO_CHAT_REQUEST
    }
    if (rejectByPeer) {
      dropdownModal = this.renderAudioChatRejectedModal()
      dropdownModalId = TB_MODALS.AUDIO_CHAT_REJECTED
    }
    return (
      <div className='tbArea'>
        <div className='tbDivider' />
        <ToolbarButton
          className={'tbAudioChat'}
          tooltip={tooltip}
          disabled={
            this.props.unstable ||
            rejectByPeer ||
            (chatRequest && requestByPeer) ||
            (chatRequest && !requestByPeer && this.props.modalStatus === 'open') ||
            (chatRequest && !requestByPeer && !this.props.modalStatus) ||
            this.props.confirmCloseMessage
          }
          onClick={() => this.handleAudioChatClick()}
          selected={chatStarted || (chatRequest && !requestByPeer)}
          dropdownModal={dropdownModal}
          dropdownModalOptions={{ id: dropdownModalId }}
        />
        {chatStarted &&
        <>
          <div className='microphoneSpacer' />
          <ToolbarButton
            className={microphoneClass}
            tooltip={microphoneTooltip}
            tooltipOptions={{ activeOnDisabled: true }}
            disabled={this.props.unstable || !chatStarted || missing}
            onClick={() => this.toggleMute()}
            selected={!muted}
          />
        </>
        }
      </div>
    )
  }

  renderPeerStatus () {
    const { peerMicrophoneMuted, stats: { audioLevelPeer } } = this.props.audio
    const className = Object
      .entries({
        'tbArea': true,
        'tbPeerStatusArea': true,
        'muted': peerMicrophoneMuted,
        // 'speaking': (!peerMicrophoneMuted && audioLevelPeer > 0.00006),
        'speaking': (!peerMicrophoneMuted && audioLevelPeer > SPEAKING_MIN_AUDIO_LEVEL),
      })
      .filter(([, truthy]) => truthy)
      .map(([className]) => className)
      .join(' ')

    return (
      <>
        <div className='tbDivider' />
        <div className={className} />
      </>
    )
  }

  render () {
    const {
      area,
      accessState,
      features: { audioChat: audioChatFeature },
      audio: { chatStarted }
    } = this.props
    if (accessState !== ACCESS_GRANTED || !audioChatFeature) {
      return null
    }

    if (area === AUDIO_CHAT_AREA.CLIENT) {
      return this.renderAudioChat()
    }

    if (area === AUDIO_CHAT_AREA.PEER && chatStarted) {
      return this.renderPeerStatus()
    }
    return null
  }
}

AudioChatComponent.contextTypes = {
  t: PropTypes.func.isRequired
}

AudioChatComponent.propTypes = {
  muteMicrophoneAction: PropTypes.func.isRequired,
  audioChatStartRequestAction: PropTypes.func.isRequired,
  audioChatStartRequestCancelAction: PropTypes.func.isRequired,
  audioChatResetPeerStatusAction: PropTypes.func.isRequired,
  audioChatEndAction: PropTypes.func.isRequired,
  accessState: PropTypes.any,
  unstable: PropTypes.bool,
  audio: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  mediaDevices: PropTypes.object.isRequired,
  confirmCloseMessage: PropTypes.bool.isRequired,
  area: PropTypes.oneOf([AUDIO_CHAT_AREA.CLIENT, AUDIO_CHAT_AREA.PEER]).isRequired,
  modalStatus: PropTypes.oneOf([null, 'open', 'hidden']),
  tbShowModalAction: PropTypes.func.isRequired,
  tbHideModalAction: PropTypes.func.isRequired,
  tbCloseModalAction: PropTypes.func.isRequired,
  mediaDevicesQueryInProgress: PropTypes.bool.isRequired,
}

export function mapStateToProps (state) {
  return {
    accessState: state.webrtcConnection.accessState,
    unstable: state.webrtcConnection.unstable,
    mediaDevices: state.mediaDevices,
    audio: state.audio,
    features: state.features,
    confirmCloseMessage: state.disconnect.confirmCloseMessage,
    modalStatus: state.toolbar.modal[TB_MODALS.AUDIO_CHAT_REQUEST],
    mediaDevicesQueryInProgress: state.mediaDevices.queryInProgress,
  }
}

function mapDispatchToProps (dispatch) {
  return {
    muteMicrophoneAction: (payload) => {
      dispatch(audioChatMuteMicrophone(payload))
    },
    audioChatStartRequestAction: (payload) => {
      dispatch(audioChatStartRequest(payload))
    },
    audioChatStartRequestCancelAction: (payload) => {
      dispatch(audioChatStartRequestCancel(payload))
    },
    audioChatResetPeerStatusAction: (payload) => {
      dispatch(audioChatResetPeerStatus(payload))
    },
    audioChatEndAction: () => {
      dispatch(audioChatEnd({ endByPeer: false }))
    },
    tbShowModalAction: (modalId) => {
      dispatch(tbShowModal(modalId))
    },
    tbHideModalAction: (modalId) => {
      dispatch(tbHideModal(modalId))
    },
    tbCloseModalAction: (modalId) => {
      dispatch(tbCloseModal(modalId))
    },
  }
}

export const AudioChat = connect(
  mapStateToProps,
  mapDispatchToProps
)(AudioChatComponent)
